import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js"
import './PlataPage.css'

// Planurile de subscripție
const SANDBOX_PLANS = {
  plus: "P-4J575883JS6144305NCYVJLQ",
  premium: "P-0GW471494K971462BNCYVITI",
}
const LIVE_PLANS = {
  plus: "P-3R772572SY047960TNCYUYXI",
  premium: "P-4TV28343F2894283NNCYU3ZI",
}

const API_BASE = "https://backend.alephmedia.ro/api"

const PlataPage = () => {
  const [searchParams] = useSearchParams()
  
  // Parametri din URL
  const email = searchParams.get('email') || ''
  const planParam = searchParams.get('plan')
  
  // State
  const [selected, setSelected] = useState(planParam === 'premium' ? 'premium' : 'plus')
  const [paypalConfig, setPaypalConfig] = useState({ mode: 'sandbox', clientId: null })
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  
  // Preluăm config PayPal din backend
  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await fetch(`${API_BASE}/payment/paypal/config`)
        const json = await res.json()
        if (json.success && json.data?.clientId) {
          setPaypalConfig({ mode: json.data.mode || 'sandbox', clientId: json.data.clientId })
        }
      } catch (e) {
        console.error('Eroare la încărcarea config PayPal:', e)
        setError('Nu s-a putut încărca configurația PayPal')
      }
    }
    loadConfig()
  }, [])

  if (success) {
    return (
      <div className="plata-page-root">
        <div className="plata-page-container">
          <div className="plata-page-header">
            <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" className="plata-page-logo" />
            <h1>Plată finalizată cu succes!</h1>
            <p>Contul tău a fost activat.</p>
          </div>
          
          <div className="plata-page-success">
            <div className="plata-page-success-icon">✓</div>
            <p>Subscripția ta <strong>{selected === 'premium' ? 'Premium' : 'Plus'}</strong> este acum activă.</p>
            <p className="plata-page-success-email">Email: <strong>{email}</strong></p>
            <p className="plata-page-success-info">
              Poți reveni în aplicația Aleph Media. Contul tău este acum activ.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Dacă nu avem email valid
  if (!email) {
    return (
      <div className="plata-page-root">
        <div className="plata-page-container">
          <div className="plata-page-header">
            <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" className="plata-page-logo" />
            <h1>Eroare</h1>
          </div>
          <div className="plata-page-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Parametrul email este obligatoriu.</span>
          </div>
          <p className="plata-page-help">
            Această pagină trebuie accesată din aplicația Aleph Media.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="plata-page-root">
      <div className="plata-page-container">
        <div className="plata-page-header">
          <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" className="plata-page-logo" />
          <h1>Alege abonamentul</h1>
          <p>Pentru contul: <strong>{email}</strong></p>
        </div>

        {error && (
          <div className="plata-page-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        <div className="plata-page-plans">
          <div
            className={`plata-page-plan${selected === 'plus' ? ' selected' : ''}`}
            onClick={() => setSelected('plus')}
            role="button"
            tabIndex={0}
          >
            <h3>Abonament Plus</h3>
            <p className="plata-page-price">4.99€ / lună</p>
            <ul>
              <li>Acces la știrile de bază</li>
              <li>Notificări importante</li>
              <li>Conținut exclusiv limitat</li>
            </ul>
            {selected === 'plus' && <div className="plata-page-checkmark">✔</div>}
          </div>

          <div
            className={`plata-page-plan${selected === 'premium' ? ' selected' : ''}`}
            onClick={() => setSelected('premium')}
            role="button"
            tabIndex={0}
          >
            <h3>Abonament Premium</h3>
            <p className="plata-page-price">9.99€ / lună</p>
            <ul>
              <li>Acces nelimitat la tot conținutul</li>
              <li>Conținut exclusiv premium</li>
              <li>Suport prioritar</li>
            </ul>
            {selected === 'premium' && <div className="plata-page-checkmark">✔</div>}
          </div>
        </div>

        <div className="plata-page-paypal">
          {paypalConfig.clientId ? (
            <PayPalScriptProvider options={{ 
              "client-id": paypalConfig.clientId, 
              vault: true, 
              locale: "ro_RO", 
              intent: "subscription" 
            }}>
              <PayPalButtons
                style={{ layout: "vertical" }}
                createSubscription={(data, actions) => {
                  const planId = (paypalConfig.mode === 'sandbox' ? SANDBOX_PLANS : LIVE_PLANS)[selected]
                  return actions.subscription.create({
                    plan_id: planId
                  })
                }}
                onApprove={async (data, actions) => {
                  setLoading(true)
                  setError(null)
                  
                  try {
                    const response = await fetch(`${API_BASE}/payment/paypal/activate-subscription`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email,
                        subscription_id: data.subscriptionID,
                        level: selected
                      })
                    })
                    
                    const result = await response.json()
                    if (result.success) {
                      setSuccess(true)
                    } else {
                      throw new Error(result.message || 'Eroare la activarea subscripției')
                    }
                  } catch (e) {
                    console.error('Eroare la activarea subscripției:', e)
                    setError(e.message || 'Eroare la activarea subscripției. Vă rugăm să contactați suportul.')
                  } finally {
                    setLoading(false)
                  }
                }}
                onError={(err) => {
                  setError('Eroare la procesarea plății: ' + (err.message || ''))
                  setLoading(false)
                }}
              />
            </PayPalScriptProvider>
          ) : (
            <div className="plata-page-loading">Se încarcă opțiunile de plată...</div>
          )}
        </div>

        {loading && (
          <div className="plata-page-loading">Se procesează plata...</div>
        )}
        
        <div className="plata-page-info">
          <p>Plata se va procesa prin PayPal în mod sigur.</p>
          <p>Subscripția se reînnoiește automat lunar.</p>
        </div>
      </div>
    </div>
  )
}

export default PlataPage
